import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Download } from "lucide-react";
import Reveal, { MaskedLine } from "../components/Reveal";
import PRODUCTS from "../data/products";

const PRODUCTS_COLORS = [
  { name: "Blue", hex: "#2B4BD8" },
  { name: "Yellow", hex: "#F2C230" },
  { name: "White", hex: "#FAFAF8", border: true },
  { name: "Red", hex: "#C8102E" },
  { name: "Green", hex: "#1E7A3C" },
];

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSize, setActiveSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setProducts(PRODUCTS);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selected) {
      const firstWithPhotos = selected.images ? selected.sizes.find((s) => selected.images[s]) : null;
      setActiveSize(firstWithPhotos || selected.sizes[0]);
      setImgIdx(0);
    }
  }, [selected]);

  const photos = selected ? (selected.images?.[activeSize] || [selected.image]) : [];

  const categories = ["All", ...new Set(products.map((p) => p.category))];
  const filtered = active === "All" ? products : products.filter((p) => p.category === active);

  return (
    <div data-testid="products-page" className="pt-32 lg:pt-40 pb-24 lg:pb-36 max-w-[1400px] mx-auto px-6 lg:px-10">
      <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald mb-6">Product Catalogue</p>
      <h1 className="font-display font-semibold tracking-tighter leading-[0.95] text-6xl sm:text-7xl lg:text-8xl">
        <MaskedLine>THE RANGE<span className="text-emerald">.</span></MaskedLine>
      </h1>
      <Reveal delay={0.15} className="flex flex-wrap items-end justify-between gap-6">
        <p className="text-obsidian/60 max-w-lg mt-8 leading-relaxed">
          Stock moulds or fully custom tooling — every pack below is manufactured in-house and can be tailored in material, colour, finish and closure.
        </p>
        <a
          href="/snp-plastech-catalogue.pdf"
          download
          data-testid="download-catalogue-btn"
          className="group inline-flex items-center gap-2 text-sm font-medium border-b border-obsidian pb-1 hover:text-emerald hover:border-emerald transition-colors duration-200 mt-8"
        >
          Download Catalogue (PDF)
          <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.5} />
        </a>
      </Reveal>

      <div className="flex flex-wrap gap-3 mt-12 mb-14" data-testid="category-filters">
        {categories.map((cat) => (
          <button
            key={cat}
            data-testid={`filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setActive(cat)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors duration-300 ${
              active === cat
                ? "bg-obsidian text-bone border-obsidian"
                : "border-line text-obsidian/70 hover:border-obsidian hover:text-obsidian"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-obsidian/50" data-testid="products-loading">Loading catalogue…</p>
      ) : (
        <motion.div layout className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="products-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                layout
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <div
                  data-testid={`product-card-${p.slug}`}
                  onClick={() => setSelected(p)}
                  className="group cursor-pointer bg-white border border-line p-6 flex flex-col h-full hover:border-obsidian transition-colors duration-300"
                >
                  <div className="flex-1 flex items-center justify-center">
                    <motion.img
                      layoutId={`product-img-${p.id}`}
                      src={p.image}
                      alt={p.name}
                      className="w-full aspect-square object-contain transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="pt-5 border-t border-line mt-5">
                    <h3 className="font-display font-medium text-lg tracking-tight leading-snug group-hover:text-emerald transition-colors duration-200">{p.name}</h3>
                    <div className="flex items-center justify-between gap-3 mt-2">
                      <p className="text-[11px] uppercase tracking-[0.15em] text-obsidian/50">{p.sizes.join(" · ")}</p>
                      <button
                        data-testid={`product-card-enquire-${p.slug}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/contact?product=${encodeURIComponent(p.name)}`); }}
                        className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald hover:text-obsidian transition-colors duration-200 inline-flex items-center gap-1 shrink-0"
                      >
                        Enquire <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-obsidian/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="bg-bone w-full max-w-4xl max-h-[90vh] overflow-y-auto grid md:grid-cols-2 border border-line"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col bg-white">
                <div className="relative min-h-[280px] flex-1 flex items-center justify-center">
                  <motion.img
                    key={photos[imgIdx]}
                    layoutId={imgIdx === 0 ? `product-img-${selected.id}` : undefined}
                    src={photos[imgIdx]}
                    alt={selected.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 w-full h-full object-contain p-8"
                  />
                </div>
                {photos.length > 1 && (
                  <div className="flex gap-2 p-4 border-t border-line" data-testid="product-gallery-thumbs">
                    {photos.map((src, i) => (
                      <button
                        key={src}
                        data-testid={`product-thumb-${i}`}
                        onClick={() => setImgIdx(i)}
                        className={`w-16 h-16 border bg-white p-1.5 transition-colors duration-200 ${i === imgIdx ? "border-obsidian" : "border-line hover:border-obsidian/40"}`}
                        aria-label={`Photo ${i + 1}`}
                      >
                        <img src={src} alt="" className="w-full h-full object-contain" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-8 lg:p-10 flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald mb-2">{selected.category}</p>
                    <h3 className="font-display font-medium text-3xl tracking-tight leading-tight">{selected.name}</h3>
                  </div>
                  <button
                    data-testid="product-modal-close"
                    onClick={() => setSelected(null)}
                    className="p-2 -mr-2 -mt-2 hover:text-emerald transition-colors duration-200"
                    aria-label="Close"
                  >
                    <X className="w-6 h-6" strokeWidth={1.5} />
                  </button>
                </div>
                <p className="text-obsidian/60 text-sm leading-relaxed mt-5">{selected.description}</p>
                <div className="mt-6">
                  <p className="text-obsidian/50 uppercase tracking-[0.15em] text-xs mb-3">Available sizes</p>
                  <div className="flex flex-wrap gap-2" data-testid="product-sizes">
                    {selected.sizes.map((s) => (
                      <button
                        key={s}
                        data-testid={`size-chip-${s.replace(/\s/g, "-")}`}
                        onClick={() => { setActiveSize(s); setImgIdx(0); }}
                        className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-200 ${
                          activeSize === s ? "bg-obsidian text-bone border-obsidian" : "border-line hover:border-obsidian"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {selected.images?.[activeSize] ? (
                    <p className="text-[11px] text-obsidian/45 mt-3 tracking-wide">
                      {selected.lineup
                        ? "Full size range shown together — colours are custom-made to order"
                        : `Showing ${activeSize} photos — colours are custom-made to order`}
                    </p>
                  ) : (
                    <p className="text-[11px] text-obsidian/45 mt-3 tracking-wide">
                      Photos of {activeSize} coming soon — ask us for samples
                    </p>
                  )}
                </div>
                <div className="mt-6 space-y-0 text-sm">
                  <div className="flex justify-between gap-6 py-3 border-b border-line items-center">
                    <span className="text-obsidian/50 uppercase tracking-[0.15em] text-xs">Colours</span>
                    <span className="flex items-center gap-2" data-testid="product-colors">
                      {PRODUCTS_COLORS.map((c) => (
                        <span
                          key={c.name}
                          title={c.name}
                          className={`w-6 h-6 rounded-full inline-block ${c.border ? "border border-line" : ""}`}
                          style={{ backgroundColor: c.hex }}
                        />
                      ))}
                    </span>
                  </div>
                  {[["Material", selected.material], ["Customisation", "Weights & colours to order · custom moulds developed"], ["Minimum order", selected.moq]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 py-3 border-b border-line">
                      <span className="text-obsidian/50 uppercase tracking-[0.15em] text-xs pt-0.5">{k}</span>
                      <span className="font-medium text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-obsidian/45 tracking-wide">
                  Standard colours shown — custom colour matching available on request
                </p>
                <button
                  data-testid="product-enquire-btn"
                  onClick={() => navigate(`/contact?product=${encodeURIComponent(selected.name)}`)}
                  className="group mt-8 inline-flex items-center justify-center gap-2 bg-obsidian text-bone font-medium text-sm px-7 py-4 rounded-full hover:bg-emerald transition-colors duration-300"
                >
                  Enquire about this product
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;
